import { logger } from '@adornis/base/logging.js';
import { getCollection, getRawCollection } from '@adornis/baseql/server/collections.js';
import { Conversation } from '../../conversation.js';
import { Exam, type ExamState } from '../../exam.js';
import {
  getPendingTaskDescs,
  hasWorkflowPendingTasks,
  isPendingTaskReady,
  type PendingTaskPriority,
} from './pending-task.js';
import { finalizeAgentTurn, runAgentStreamingLoop } from '../utils/agent-loop.js';
// Side-effect: registers GradingFinishedPendingTask desc builder
import './grading-finished.task.js';
// Side-effect: registers ExamCreationFinishedPendingTask desc builder
import './exam-creation-finished.task.js';

export type WorkflowEmit = (conversation: Conversation) => void;
/** Final-only sink: discards streamed previews. The final state still lands in the DB. */
const NOOP_EMIT: WorkflowEmit = () => {};

/**
 * Builds the trigger message that opens the workflow pass. We embed the concrete task
 * description(s) right here — not just a "look at the context" pointer — because otherwise the
 * agent tends to latch onto the most recent conversation step (e.g. a task structure it just
 * confirmed) and treat THAT as the workflow task, ignoring the actual pending task that's only
 * present in the system-prompt context. As the most recent message, this is the strongest signal.
 */
function buildWorkflowNotification(workflowTasks: Array<Record<string, unknown>>): string {
  const descs = getPendingTaskDescs(workflowTasks);
  return (
    'SYSTEM: Eine oder mehrere Hintergrundaufgaben wurden soeben abgeschlossen und müssen JETZT bearbeitet werden. ' +
    'Bearbeite ausschließlich die unten aufgeführte(n) Aufgabe(n). Führe KEINE bereits erledigten Schritte aus dem ' +
    'bisherigen Gesprächsverlauf erneut aus (z.B. eine bereits erfolgte Bestätigung der Aufgabenstruktur) und ' +
    'verwechsle sie nicht mit der hier genannten Aufgabe.\n\n' +
    (descs || 'Sieh dir die ausstehenden Hintergrundaufgaben im Kontext an und handle entsprechend.')
  );
}

// Grace before the idle fallback runs. Gives a subscribed client's standing subscription time to
// detect the task via the exam change stream and claim the lock first (so it streams live).
const FALLBACK_GRACE_MS = 3000;

/**
 * Create a workflow-priority pending task and ensure it gets processed — without any in-memory,
 * instance-local routing (stateless):
 *  - If a client is watching, its `streamConversationWorkflowSubscription` resolver sees the task
 *    appear on the exam change stream and runs the pass live on the client's own instance
 *    (co-located streaming, no per-chunk DB writes).
 *  - Otherwise this scheduled fallback runs the pass after a short grace and just writes the final
 *    message to the DB (delivered whenever a client next loads, via the conversation change stream).
 * The atomic thinking-lock dedupes the two; the grace gives the streaming client priority.
 */
export async function pushWorkflowTask(
  examId: string,
  conversationId: string,
  taskDoc: Record<string, unknown>,
): Promise<void> {
  const examCollection = await getCollection(Exam as any);
  await examCollection.updateOne({ _id: examId } as any, { $push: { 'examState.pendingTasks': taskDoc } as any });

  scheduleWorkflowAgentFallback(conversationId, examId);
}

/**
 * Schedule the idle fallback pass after the grace window: runs the workflow agent and writes the
 * final message to the DB when no client is subscribed to stream it live. Use this when a workflow
 * task is already on the exam (e.g. released by updateSetupStep) and just needs to be driven; the
 * atomic thinking-lock dedupes it against a subscribed client's live pass.
 */
export function scheduleWorkflowAgentFallback(conversationId: string, examId: string): void {
  setTimeout(() => {
    void runWorkflowAgentPass(conversationId, examId, NOOP_EMIT).catch(err =>
      logger.error({ err, conversationId, context: 'WorkflowAgent' }, 'Idle fallback pass failed'),
    );
  }, FALLBACK_GRACE_MS);
}

/**
 * Run one workflow pass: drain the exam's workflow-priority pending tasks through the shared
 * streaming agentic loop, streaming previews to `emit` (subscriber.next when a client's standing
 * subscription drives this; a no-op for the idle fallback). Guarded by the atomic thinking-lock so
 * it never overlaps a user turn or another pass.
 */
export async function runWorkflowAgentPass(
  conversationId: string,
  examId: string | null | undefined,
  emit: WorkflowEmit,
): Promise<void> {
  if (!conversationId) return;

  const conversationRaw = await getRawCollection(Conversation._collectionName);

  // Atomic lock: flip thinking false→true only if not already thinking. matchedCount===0 means a
  // user turn or another pass holds it — bail; whoever holds it re-checks for tasks when it releases.
  const lock = await conversationRaw.updateOne({ _id: conversationId, thinking: { $ne: true } } as any, {
    $set: { thinking: true },
  });
  if (!lock.matchedCount) {
    logger.info({ conversationId, context: 'WorkflowAgent' }, 'Conversation is busy — another turn/pass holds the lock');
    return;
  }

  let resolvedExamId: string | undefined;
  let processedAnything = false;
  let lockReleased = false;

  try {
    const conversationCollection = await getCollection(Conversation);
    const examCollection = await getCollection(Exam as any);

    const rawConv = await conversationCollection.findOne({ _id: conversationId });
    if (!rawConv) {
      logger.warn({ conversationId, context: 'WorkflowAgent' }, 'Conversation not found');
      return;
    }

    resolvedExamId = (examId ?? String((rawConv as any).examId ?? '').trim()) || undefined;
    if (!resolvedExamId) {
      logger.info({ conversationId, context: 'WorkflowAgent' }, 'No exam linked to conversation');
      return;
    }

    const exam = await examCollection.findOne({ _id: resolvedExamId } as any);
    const allTasks: Array<Record<string, unknown>> = Array.isArray((exam as any)?.examState?.pendingTasks)
      ? (exam as any).examState.pendingTasks
      : [];
    // Only ready workflow tasks are surfaced and removed: a task held back (isReadyForAgent:false,
    // e.g. a SetupStep still accumulating finished steps) is left untouched until it releases.
    const workflowTasks = allTasks.filter(
      t => (t?.priority as PendingTaskPriority) === 'workflow' && isPendingTaskReady(t),
    );

    if (!workflowTasks.length) {
      logger.info({ resolvedExamId, context: 'WorkflowAgent' }, 'No workflow tasks for exam');
      return;
    }

    // All currently-pending workflow tasks are surfaced together in the exam context, so one pass
    // handles them all. Capture their classes to remove only after the pass succeeds.
    const workflowTaskClasses = [...new Set(workflowTasks.map(t => String(t?._class ?? '')).filter(Boolean))];
    const currentExamState = ((exam as any)?.examState ?? {}) as ExamState;
    const language = String((rawConv as any).language ?? 'Deutsch');
    const existingMessages: any[] = Array.isArray((rawConv as any).messages) ? (rawConv as any).messages : [];

    // Signal thinking to the streaming client immediately. Spread the full conversation so the
    // client's shallow merge keeps examId/suggestions/etc. intact.
    emit(new Conversation({ ...(rawConv as any), messages: existingMessages, thinking: true }));

    const loopResult = await runAgentStreamingLoop({
      conversationId,
      examId: resolvedExamId,
      exam,
      examState: currentExamState,
      language,
      messages: existingMessages,
      trailingMessages: [{ role: 'user', content: buildWorkflowNotification(workflowTasks) }],
      existingConversation: rawConv as any,
      emit,
    });
    processedAnything = true;

    // Pass succeeded — remove the workflow tasks it handled so they don't reprocess.
    for (const cls of workflowTaskClasses) {
      await examCollection.updateOne({ _id: resolvedExamId } as any, {
        $pull: { 'examState.pendingTasks': { _class: cls } } as any,
      });
    }

    // Persists the final message + releases the thinking-lock (thinking:false), delivered to every
    // client via the conversation change stream regardless of which instance ran the pass.
    await finalizeAgentTurn({
      conversationId,
      existingConversation: rawConv as any,
      examId: resolvedExamId,
      language,
      loopResult,
      thinking: false,
      emit,
    });
    lockReleased = true;

    // Grading changed exam/submission data — nudge clients to reload it.
    await examCollection.updateOne({ _id: resolvedExamId } as any, { $set: { 'examState.uiNeedsRefresh': true } });
  } catch (err) {
    logger.error({ err, conversationId, context: 'WorkflowAgent' }, 'Failed for conversation');
  } finally {
    // Safety net: release the lock if the success path didn't (error / no-task / no exam).
    if (!lockReleased) {
      await conversationRaw.updateOne({ _id: conversationId } as any, { $set: { thinking: false } });
    }
  }

  // A new workflow task may have arrived while we held the lock. The lock is free now, so drain it.
  if (processedAnything && resolvedExamId) {
    try {
      const examCollection = await getCollection(Exam as any);
      const exam = await examCollection.findOne({ _id: resolvedExamId } as any);
      const pendingTasks: Array<Record<string, unknown>> = Array.isArray((exam as any)?.examState?.pendingTasks)
        ? (exam as any).examState.pendingTasks
        : [];
      if (hasWorkflowPendingTasks(pendingTasks)) {
        logger.info({ resolvedExamId, context: 'WorkflowAgent' }, 'More workflow tasks pending — re-running');
        void runWorkflowAgentPass(conversationId, resolvedExamId, emit);
      }
    } catch (err) {
      logger.error({ err, conversationId, context: 'WorkflowAgent' }, 'Re-check failed for conversation');
    }
  }
}
